export const OTP_SESSION_TTL_SEC = 60;
export const MAGIC_OTP_LIFESPAN_SEC = 604800;

const OTP_SESSION_KEY = 'magic_otp_session';

interface OtpSession {
  email: string;
  sentAt: number;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function saveOtpSession(email: string): void {
  sessionStorage.setItem(
    OTP_SESSION_KEY,
    JSON.stringify({
      email: normalizeEmail(email),
      sentAt: Date.now(),
    } satisfies OtpSession)
  );
}

export function getOtpSession(): OtpSession | null {
  const raw = sessionStorage.getItem(OTP_SESSION_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as OtpSession;
  } catch {
    return null;
  }
}

export function isOtpSessionActiveForEmail(email: string): boolean {
  const session = getOtpSession();
  if (!session) return false;
  if (session.email !== normalizeEmail(email)) return false;

  const elapsed = Math.floor((Date.now() - session.sentAt) / 1000);
  return elapsed < OTP_SESSION_TTL_SEC;
}

export function canReuseOtpSession(
  email: string,
  tokenPromise: unknown
): boolean {
  return Boolean(tokenPromise) && isOtpSessionActiveForEmail(email);
}

export function markOtpSent(email: string): void {
  saveOtpSession(email);
}

export function getOtpResendCooldown(): number {
  const session = getOtpSession();
  if (!session) return 0;

  const elapsed = Math.floor((Date.now() - session.sentAt) / 1000);
  return Math.max(0, OTP_SESSION_TTL_SEC - elapsed);
}

export function clearOtpSessionStorage(): void {
  sessionStorage.removeItem(OTP_SESSION_KEY);
}

function isMagicUserCanceledError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    message.includes('User canceled') ||
    message.includes('canceled action') ||
    message.includes('cancelled action')
  );
}

/** Attach early so Magic cancel rejections never become uncaught runtime errors. */
export function prepareMagicOtpToken<T>(tokenPromise: T): T {
  suppressMagicCancelRejection(tokenPromise);
  return tokenPromise;
}

/** Prevent Magic's expected cancel rejection from surfacing as an uncaught error. */
function suppressMagicCancelRejection(tokenPromise: any): void {
  if (tokenPromise && typeof tokenPromise.catch === 'function') {
    tokenPromise.catch((error: unknown) => {
      if (!isMagicUserCanceledError(error)) {
        console.warn('Magic OTP session error:', error);
      }
    });
  }
}

/**
 * Cancel an in-flight Magic email OTP session and wait for it to settle.
 * Magic only allows one active loginWithEmailOTP flow at a time.
 */
export async function cancelMagicOtpSession(tokenPromise: any): Promise<void> {
  if (!tokenPromise) return;

  suppressMagicCancelRejection(tokenPromise);

  await new Promise<void>(resolve => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    tokenPromise
      .on('settled', done)
      // @ts-ignore Magic PromiEvent error payload
      ?.on('error', (error: unknown) => {
        if (!isMagicUserCanceledError(error)) {
          console.warn('Magic OTP session error:', error);
        }
        done();
      });

    try {
      tokenPromise.emit('cancel');
    } catch (error) {
      if (!isMagicUserCanceledError(error)) {
        console.warn('Magic OTP cancel error:', error);
      }
      done();
    }

    setTimeout(done, 3000);
  });
}
